/**
 * Portable JSON representation of a workflow — one version's graph plus the
 * lineage-level settings needed to recreate it elsewhere.
 *
 * This is the single format shared by every way a workflow leaves or enters the
 * system: the editor's Export/Import buttons, the definition overview tab, and
 * the local `workflow` CLI (`src/scripts/workflows/workflow-cli.ts`). They all
 * parse with {@link parseWorkflowBundle} and re-key with
 * {@link remapWorkflowGraphIds}, so a file written by one is readable by the
 * others.
 *
 * The graph reuses the very schemas the oRPC contract uses
 * (`StepDefinitionSchema` / `EdgeDefinitionSchema` / `StepLayoutGroupSchema`),
 * so a bundle's `steps`/`edges` are exactly what `workflows.createDefinition`
 * accepts. That is what makes export → edit → import a round trip rather than a
 * translation.
 *
 * Lives in `@erp/workflows` rather than in the backend or the frontend because
 * both sides read and write it. It is pure Zod with no id generation of its own
 * (`remapWorkflowGraphIds` takes an id factory), so it stays safe to bundle
 * into the Temporal workflow sandbox through this package's barrel.
 */

import { z } from "zod";

import {
  EdgeDefinitionSchema,
  StepDefinitionSchema,
  StepLayoutGroupSchema,
  ZipTieSchema,
  type ZipTie,
  type EdgeDefinition,
  type StepDefinition,
  type StepLayoutGroup,
} from "./types";
import { remapWorkflowExpressionStepIds } from "./expressions";
import { remapActionBoundaryPorts } from "./utils/action-interfaces";
import { remapZipTieStepIds } from "./utils/zipTies";

/**
 * Bumped only on a breaking change to the bundle shape. Parsing rejects a
 * bundle it does not understand rather than guessing.
 */
export const WORKFLOW_BUNDLE_FORMAT_VERSION = 1;

/**
 * Step ids must be unique within a file.
 *
 * `remapWorkflowGraphIds` keys its remap by the *old* id, so two steps sharing
 * one id collapse to a single map entry and both get handed the same new id —
 * the re-keying would carry the duplicate straight through and it would land as
 * a primary-key violation on save. Reject it up front, naming the offender,
 * rather than letting a Postgres error reach the user.
 *
 * Edge ids need no such check: every edge is re-keyed individually, so
 * duplicates there are resolved rather than propagated.
 */
function assertUniqueStepIds(
  steps: readonly { id: string }[],
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  steps.forEach((step, index) => {
    if (seen.has(step.id)) {
      ctx.addIssue({
        code: "custom",
        path: [index, "id"],
        message: `duplicate step id ${step.id} — step ids must be unique within the file`,
      });
      return;
    }
    seen.add(step.id);
  });
}

/** The graph itself — the part that becomes a workflow definition version. */
const WorkflowBundleGraphShape = {
  steps: z.array(StepDefinitionSchema).superRefine(assertUniqueStepIds),
  edges: z.array(EdgeDefinitionSchema),
  /**
   * Visual step layout groups. Defaulted so files exported before they existed
   * (and hand-written ones) still parse, and so readers never branch on
   * undefined.
   */
  stepLayoutGroups: z.array(StepLayoutGroupSchema).prefault([]),
  /** Visual straps bundling wires. Absent on files written before zip ties. */
  zipTies: z.array(ZipTieSchema).prefault([]),
};

/**
 * Lineage-level (Workflow row) settings. Version-independent: these apply to
 * every version of the workflow, which is why they sit beside the graph rather
 * than inside it.
 */
export const WorkflowBundleLineageSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  /** Whether triggers fire. Import defaults this off — see `importBundle`. */
  isActive: z.boolean().prefault(false),
  isComponent: z.boolean().prefault(false),
  exposedInputs: z.array(z.string()).nullable().optional(),
  exposedOutputs: z.array(z.string()).nullable().optional(),
  fanoutStrategy: z.enum(["parallel", "sequential"]).prefault("parallel"),
  retryConfig: z.record(z.string(), z.unknown()).nullable().optional(),
  /**
   * Manager agent for the workflow. Carried because the runtime propagates it
   * into step execution, so dropping it silently changes behavior. It is an
   * org-scoped id: import resolves it in the target organization and refuses
   * rather than clearing it when it doesn't resolve. See `importBundle`.
   */
  workflowManagerAgentDefinitionId: z.string().nullable().optional(),
  /**
   * Orchestrator the lineage runs on. There is exactly one today, so a bundle
   * cannot ask for an engine the target doesn't have and import has nothing to
   * apply; it is carried so the field survives a round trip and so adding a
   * second orchestrator is a schema change here rather than a format change.
   */
  orchestratorType: z.literal("temporal").optional(),
});

export type WorkflowBundleLineage = z.infer<typeof WorkflowBundleLineageSchema>;

/** Provenance, for humans reading a diff. Never used to resolve anything. */
export const WorkflowBundleSourceSchema = z.object({
  organizationId: z.string().optional(),
  workflowId: z.string().optional(),
  workflowDefinitionId: z.string().optional(),
  version: z.number().optional(),
  status: z.string().optional(),
  /**
   * How a name-only export chose its version: the caller's own open draft, the
   * pinned version, or the newest finalized one. `requested_version` when the
   * caller named a version or definition id.
   */
  resolvedAs: z
    .enum(["your_draft", "pinned", "latest_finalized", "requested_version"])
    .optional(),
  exportedAt: z.string().optional(),
});

export const WorkflowBundleSchema = z.object({
  formatVersion: z.literal(WORKFLOW_BUNDLE_FORMAT_VERSION),
  source: WorkflowBundleSourceSchema.optional(),
  workflow: WorkflowBundleLineageSchema,
  ...WorkflowBundleGraphShape,
});

export type WorkflowBundle = z.infer<typeof WorkflowBundleSchema>;

/** The graph slice of a bundle, as the re-keying helper sees it. */
export type WorkflowBundleGraph = Pick<
  WorkflowBundle,
  "steps" | "edges" | "stepLayoutGroups" | "zipTies"
>;

/**
 * The flat shape: lineage fields at the root, no envelope. Two things use it —
 * the editor's Export button before the bundle envelope existed (so users have
 * these files on disk), and the chat agent's `propose_workflow` payload, which
 * the editor renders on the canvas without ever writing a file. It is also the
 * easiest shape to hand-write, so import keeps accepting it indefinitely rather
 * than treating it as deprecated.
 */
export const FlatWorkflowExportSchema = z.object({
  name: z.string().min(1).max(255),
  /** JSON often sends `null` for optional strings; coerce before `z.string()`. */
  description: z.preprocess(
    (v) => (v === null ? undefined : v),
    z.string().optional(),
  ) as unknown as z.ZodOptional<z.ZodString>,
  orchestratorType: z.literal("temporal").optional(),
  ...WorkflowBundleGraphShape,
  /** Optional here, unlike in a bundle: absent means "this file predates groups". */
  stepLayoutGroups: z.array(StepLayoutGroupSchema).optional(),
  zipTies: z.array(ZipTieSchema).optional(),
});

type InferredFlatWorkflowExport = z.infer<typeof FlatWorkflowExportSchema>;
export type FlatWorkflowExport = Omit<
  InferredFlatWorkflowExport,
  "description"
> & { description?: string };

/**
 * A file is flat when it names the workflow at the root and has no `workflow`
 * object. Distinguishing the two up front matters for error messages: parsing a
 * flat file against the bundle schema reports "workflow: Required" and nothing
 * about the actual mistake in `steps`.
 */
function looksFlat(raw: unknown): boolean {
  if (typeof raw !== "object" || raw === null) return false;
  const record = raw as Record<string, unknown>;
  return !("workflow" in record) && typeof record.name === "string";
}

/** Lift a flat export into a bundle, defaulting every lineage field it lacks. */
export function flatExportToBundle(flat: FlatWorkflowExport): WorkflowBundle {
  return buildWorkflowBundle({
    workflow: {
      name: flat.name,
      description: flat.description,
      orchestratorType: flat.orchestratorType,
    },
    steps: flat.steps,
    edges: flat.edges,
    stepLayoutGroups: flat.stepLayoutGroups,
    zipTies: flat.zipTies,
  });
}

/**
 * Flatten a bundle for consumers that work in the flat shape — the workflow
 * editor and the chat agent. Lineage fields with no flat equivalent (activation
 * state, component settings, manager agent) are dropped: nothing on the canvas
 * reads them, and the editor never writes them back.
 */
export function bundleToFlatExport(bundle: WorkflowBundle): FlatWorkflowExport {
  const { workflow } = bundle;
  return {
    name: workflow.name,
    ...(workflow.description != null
      ? { description: workflow.description }
      : {}),
    ...(workflow.orchestratorType
      ? { orchestratorType: workflow.orchestratorType }
      : {}),
    steps: bundle.steps,
    edges: bundle.edges,
    // Omitted rather than sent as `[]` so a file without them survives
    // flat → bundle → flat unchanged.
    ...(bundle.stepLayoutGroups.length > 0
      ? { stepLayoutGroups: bundle.stepLayoutGroups }
      : {}),
    ...(bundle.zipTies.length > 0 ? { zipTies: bundle.zipTies } : {}),
  };
}

/**
 * Build a bundle from the pieces a caller has on hand, filling every lineage
 * field it doesn't. Used by export paths that only know the graph plus a name.
 */
export function buildWorkflowBundle(input: {
  workflow: { name: string } & Partial<WorkflowBundleLineage>;
  source?: z.infer<typeof WorkflowBundleSourceSchema>;
  steps: StepDefinition[];
  edges: EdgeDefinition[];
  stepLayoutGroups?: StepLayoutGroup[];
  zipTies?: ZipTie[];
}): WorkflowBundle {
  return {
    formatVersion: WORKFLOW_BUNDLE_FORMAT_VERSION,
    ...(input.source ? { source: input.source } : {}),
    workflow: WorkflowBundleLineageSchema.parse(input.workflow),
    steps: input.steps,
    edges: input.edges,
    stepLayoutGroups: input.stepLayoutGroups ?? [],
    zipTies: input.zipTies ?? [],
  };
}

/** Flatten Zod issues into `path: message` strings a CLI or toast can print. */
function formatIssues(error: z.ZodError): string[] {
  return error.issues.map(
    (issue) =>
      `${issue.path.length > 0 ? issue.path.join(".") : "<root>"}: ${issue.message}`,
  );
}

/**
 * Parse untrusted JSON into a bundle, accepting either the current envelope or
 * the legacy flat export, and returning either the bundle or a flat list of
 * `path: message` strings. Callers surface those directly — a CLI or a toast
 * printing raw Zod issues is unreadable.
 */
export function parseWorkflowBundle(
  raw: unknown,
): { ok: true; bundle: WorkflowBundle } | { ok: false; errors: string[] } {
  if (looksFlat(raw)) {
    const flat = FlatWorkflowExportSchema.safeParse(raw);
    return flat.success
      ? { ok: true, bundle: flatExportToBundle(flat.data) }
      : { ok: false, errors: formatIssues(flat.error) };
  }

  const parsed = WorkflowBundleSchema.safeParse(raw);
  return parsed.success
    ? { ok: true, bundle: parsed.data }
    : { ok: false, errors: formatIssues(parsed.error) };
}

/**
 * Rewrite an input socket's expression to point at the step's new id. A
 * malformed expression can't be reparsed — leave it untouched rather than
 * failing the whole import; the editor already surfaces it as invalid.
 */
function remapSocketExpression(
  expression: string,
  stepIdMap: ReadonlyMap<string, string>,
): string {
  try {
    return remapWorkflowExpressionStepIds(expression, stepIdMap);
  } catch {
    return expression;
  }
}

/** Reserved config key mirroring the editor's socket-override staging area. */
const MANUAL_EXPRESSION_OVERRIDES_CONFIG_KEY = "__manual_expression_overrides";

/**
 * Rewrite step ids inside `config.__manual_expression_overrides`.
 *
 * Exported because every path that re-keys a graph has to call it, not just
 * import: cloning a definition into a new version re-keys steps too, and
 * missing it there resurrects the old ids on the next canvas save exactly as
 * missing it on import did.
 *
 * The map is `socketId -> expression`, holding the same `(output "<stepId>" …)`
 * text as the socket it stages. Socket ids are left alone — they are scoped to
 * their step, like every other socket id here.
 *
 * Untyped on purpose: `config` is an opaque `Record<string, unknown>` at this
 * layer, so anything that is not a string-valued object is passed through
 * rather than coerced.
 */
export function remapManualExpressionOverrides(
  config: Record<string, unknown>,
  stepIdMap: ReadonlyMap<string, string>,
): Record<string, unknown> {
  const raw = config[MANUAL_EXPRESSION_OVERRIDES_CONFIG_KEY];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return config;

  const remapped: Record<string, unknown> = {};
  for (const [socketId, expression] of Object.entries(
    raw as Record<string, unknown>,
  )) {
    remapped[socketId] =
      typeof expression === "string"
        ? remapSocketExpression(expression, stepIdMap)
        : expression;
  }
  return { ...config, [MANUAL_EXPRESSION_OVERRIDES_CONFIG_KEY]: remapped };
}

/**
 * Give every imported step and edge a fresh id, rewriting every reference to
 * the old ones. `newId` is injected so this module needs no id generator of its
 * own (see the file header) and so tests can be deterministic.
 *
 * Step and edge ids are primary keys in `wfv2_workflow_definition_steps` /
 * `_edges`, unique across every workflow in the database — not just the one
 * being edited. An exported file carries the ids of the rows it came from, so
 * importing it while those rows still exist (the source workflow, a copy
 * imported earlier, a file shared from elsewhere) collides on insert.
 *
 * References that must follow the remap:
 * - `edge.sourceStepId` / `edge.targetStepId`
 * - action-boundary `config.ports[].boundStepId` (via `remapActionBoundaryPorts`)
 * - input socket `expression` values, which embed `(output "<stepId>" "<socket>")`
 * - `config.__manual_expression_overrides` — the editor's staging copy of those
 *   same expressions, keyed by socket id. Remapping only the socket half is
 *   worse than remapping neither: the import looks clean, then the editor's
 *   `graphToDefinition` reads this key back on the next save and it OVERRIDES
 *   the socket, resurrecting the old step id as
 *   `Expression references unknown step "<old id>"`. The CLI path hides the
 *   bug — the server rederives this key from the authoritative sockets — so it
 *   only ever surfaced after a round trip through the canvas.
 * - `stepLayoutGroups[].stepIds` — miss these and the server's
 *   `pruneStepLayoutGroupMembers` drops every member as unknown, so groups
 *   import as empty boxes rather than failing loudly
 * - `zipTies[].wires` — miss the step ids inside them and every strap still
 *   points at the source workflow's steps, so the imported copy draws nothing
 *
 * Two kinds of id are deliberately left alone:
 * - Socket ids: field names scoped to their step, not global keys.
 * - Layout group `id` / `parentGroupId`: `stepLayoutGroups` is a JSONB column on
 *   the definition row, so group ids are scoped to that row and can't collide
 *   with another definition's. Leaving them fixed also keeps `parentGroupId`
 *   nesting intact for free.
 */
export function remapWorkflowGraphIds(
  graph: WorkflowBundleGraph,
  newId: () => string,
): WorkflowBundleGraph {
  const stepIdMap = new Map<string, string>(
    graph.steps.map((step) => [step.id, newId()]),
  );
  const newStepId = (id: string): string => stepIdMap.get(id) ?? id;

  const steps = remapActionBoundaryPorts(graph.steps, stepIdMap).map(
    (step) => ({
      ...step,
      id: newStepId(step.id),
      config: remapManualExpressionOverrides(step.config, stepIdMap),
      sockets: {
        inputs: step.sockets.inputs.map((socket) => ({
          ...socket,
          expression: socket.expression
            ? remapSocketExpression(socket.expression, stepIdMap)
            : undefined,
        })),
        outputs: step.sockets.outputs,
      },
    }),
  );

  // An edge referencing a step that isn't in the file is already dangling —
  // keep the original reference so validation reports it instead of hiding it
  // behind a freshly minted one.
  const edges = graph.edges.map((edge) => ({
    ...edge,
    id: newId(),
    sourceStepId: newStepId(edge.sourceStepId),
    targetStepId: newStepId(edge.targetStepId),
  }));

  const stepLayoutGroups = graph.stepLayoutGroups.map((group) => ({
    ...group,
    stepIds: group.stepIds.map(newStepId),
  }));

  return {
    steps,
    edges,
    stepLayoutGroups,
    zipTies: remapZipTieStepIds(graph.zipTies, stepIdMap),
  };
}
